import { useEffect, useState } from 'react';
import { Campus, CampusFacility, fetchCampusByFacility } from 'api/campus';

// Campus membership is near-static (cached 24h server-side): one lookup per facility per page load.
const campusCache = new Map<string, Promise<Campus | null>>();

function loadCampus(facilityId: string): Promise<Campus | null> {
  let pending = campusCache.get(facilityId);
  if (!pending) {
    pending = fetchCampusByFacility(facilityId).catch((error) => {
      campusCache.delete(facilityId);
      throw error;
    });
    campusCache.set(facilityId, pending);
  }
  return pending;
}

interface LoadedCampus {
  anchorId: string;
  campus: Campus | null;
  error: string | null;
}

export interface UseCampusResult {
  campus: Campus | null;
  /** The campus's facilities; just the anchor for a facility with no campus. */
  campusFacilities: CampusFacility[];
  isMultiFacility: boolean;
  loading: boolean;
  error: string | null;
}

/** The campus of the selected (anchor) facility. */
export function useCampus(
  anchorFacility:
    | { id: number | string; name: string; facility_alias?: string | null }
    | null
    | undefined
): UseCampusResult {
  const anchorId = anchorFacility ? String(anchorFacility.id) : null;
  const anchorName = anchorFacility?.name || '';
  const anchorAlias = anchorFacility?.facility_alias ?? null;
  // Tagged with the facility it was loaded for, so a new anchor reads as loading
  // on its first render instead of returning the previous facility's campus.
  const [loaded, setLoaded] = useState<LoadedCampus | null>(null);

  useEffect(() => {
    if (!anchorId) return;

    let cancelled = false;

    loadCampus(anchorId)
      .then((result) => {
        if (!cancelled) setLoaded({ anchorId, campus: result, error: null });
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load campus:', err);
          setLoaded({
            anchorId,
            campus: null,
            error: err.message || 'Failed to load campus'
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [anchorId]);

  const current = loaded && loaded.anchorId === anchorId ? loaded : null;
  const campus = current?.campus ?? null;
  const loading = !!anchorId && !current;

  const campusFacilities: CampusFacility[] =
    campus && campus.facilities.length > 0
      ? campus.facilities
      : anchorId
        ? [{ id: anchorId, name: anchorName, facility_alias: anchorAlias }]
        : [];

  return {
    campus,
    campusFacilities,
    isMultiFacility: campusFacilities.length > 1,
    loading,
    error: current?.error ?? null
  };
}
